import { useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { MapPin, Loader2, Sparkles, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminHubSuggestions() {
  const [city, setCity] = useState("");
  const [minOrders, setMinOrders] = useState(1);
  const [topN, setTopN] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const params = { min_orders_per_day: minOrders, top_n: topN };
      if (city.trim()) params.city = city.trim();
      const { data } = await api.get("/admin/hub-suggestions/", { params });
      setData(data);
      if (!data.candidates?.length) toast("No high-density gaps found", { description: data.warning || "Try lowering the threshold or pick a different city." });
    } catch (e) {
      // Sprint 81.2 — surface the actual server detail instead of a generic
      // "Failed to load suggestions". Most failures here are 422 validation
      // errors (e.g. min_orders_per_day too high) — opaque toasts left the
      // user guessing.
      const d = e?.response?.data?.detail;
      const detail = Array.isArray(d)
        ? d.map((x) => x.msg || x.detail || JSON.stringify(x)).join(" · ")
        : (typeof d === "string" ? d : (e?.message || "Failed to load suggestions"));
      toast.error("Failed to load suggestions", { description: detail });
    } finally { setLoading(false); }
  };

  const openCreate = (s) => {
    // Hand off to existing /dashboard/hubs page with prefilled coords via query string
    const qs = new URLSearchParams({ lat: s.lat, lng: s.lng, name: `New micro hub @ ${city || "city"}` }).toString();
    window.location.href = `/dashboard/hubs?${qs}`;
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="admin-hub-suggestions-page">
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Sparkles className="h-7 w-7 text-purple-500" /> AI Hub Placement Suggestions
        </h1>
        <p className="text-sm text-slate-500 mt-1">Find high-demand zones that are far from your existing hubs. Powered by your real pickup data + GPT reasoning.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="hub-sug-controls">
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">City filter</label>
            <input
              type="text" placeholder="e.g. Bangalore"
              value={city} onChange={(e) => setCity(e.target.value)}
              className="w-full mt-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm"
              data-testid="hub-sug-city"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Min orders/day</label>
            <input
              type="number" min="1" max="10000" value={minOrders}
              onChange={(e) => setMinOrders(parseInt(e.target.value || "1", 10))}
              className="w-full mt-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm"
              data-testid="hub-sug-min-orders"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Top N</label>
            <input
              type="number" min="1" max="10" value={topN}
              onChange={(e) => setTopN(parseInt(e.target.value || "3", 10))}
              className="w-full mt-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm"
              data-testid="hub-sug-top-n"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={fetchSuggestions} disabled={loading} className="w-full bg-[#FF6B00] hover:bg-orange-600 gap-2" data-testid="hub-sug-run">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate suggestions
            </Button>
          </div>
        </div>
      </div>

      {data && (
        <>
          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-3" data-testid="hub-sug-meta">
            <span><b className="text-slate-900">{data.data_points}</b> pickup points analysed · last {data.lookback_days} days</span>
            <span>· <b className="text-slate-900">{data.candidate_cells}</b> candidate cells</span>
            <span>· <b className="text-slate-900">{data.existing_hubs?.length || 0}</b> existing hubs</span>
          </div>

          {data.warning ? (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800" data-testid="hub-sug-warning">
              {data.warning}
            </div>
          ) : null}

          {data.city_demand_summary?.length ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="city-demand-summary">
              <h2 className="font-['Outfit'] text-base font-semibold text-slate-900">City-level demand (last 90 days)</h2>
              <p className="text-xs text-slate-500 mt-0.5">Fallback view while pickups don't carry lat/lng yet.</p>
              <ul className="mt-3 divide-y divide-slate-50">
                {data.city_demand_summary.map((c, i) => (
                  <li key={i} className="py-2.5 flex items-center justify-between text-sm">
                    <div>
                      <p className="font-semibold text-slate-900">{c.city}</p>
                      <p className="text-xs text-slate-400">{c.order_count} orders · ₹{Math.round(c.gmv_inr).toLocaleString()} GMV</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${i === 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {i === 0 ? "Top demand" : `#${i + 1}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {(data.candidates || []).map((s, idx) => (
            <div key={`${s.lat}-${s.lng}`} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid={`hub-sug-card-${idx}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white font-bold text-lg shrink-0">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-['Outfit'] text-lg font-semibold text-slate-900">
                      Candidate location · ({s.lat}, {s.lng})
                    </p>
                    <p className="text-sm text-slate-600 mt-1">{s.rationale}</p>
                  </div>
                </div>
                <Button onClick={() => openCreate(s)} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid={`hub-sug-create-${idx}`}>
                  <Building2 className="h-4 w-4" /> Create hub here
                </Button>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Orders (90d)" value={s.order_count} />
                <Stat label="GMV" value={`₹${Math.round(s.gmv_inr).toLocaleString()}`} />
                <Stat label="Nearest hub" value={`${s.distance_to_nearest_hub_km} km`} sub={s.nearest_hub_name || "—"} />
                <Stat label="Score" value={s.score} />
              </div>
              <a
                href={`https://www.google.com/maps?q=${s.lat},${s.lng}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 mt-3 text-xs text-blue-600 hover:underline"
              >
                <MapPin className="h-3 w-3" /> View on Google Maps
              </a>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p className="text-base font-bold text-slate-900 mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}
